/* eslint-disable react-hooks/exhaustive-deps */
import { MouseEvent, useEffect, useRef, useState } from "react";
import { TApiTechnician } from "../../../lib/types/dentalTechs";
import { useTechnicians } from "../../../services/technicians";
import { useRouteNavigation } from "../../../lib/common/routes";
import { useFileManagement } from "../../../services/file-management";
import { DentalTechSearchFilterPanel, TTechnicianFilterSettings } from "../../common/dental-tech-search-filter-panel";
import { useUserService } from "../../../services/users";
import { TGenericGUIDName, TGenericIdName } from "../../../lib/types/general";
import { getCacheKillQueryString } from "../../../lib/common/api";
import { ASSETS_ROUTES, TECH_SEARCH_PAGINATION_SIZE } from "../../../lib/common/constants";
import { Pagination } from "../../common/pagination";
import { Spinner } from "../../common/spinner";
import { useNotificationContext } from "../../../contexts/notification-context";
import { MessageBtn } from "../../common/message-button";
import { LogoTech } from "../../logo";

type TDashboardDentalTechRowProps = {
    userInfo: TApiTechnician;
    regionList: TGenericGUIDName[];
    industryRoles: TGenericIdName[];
    refFilter?: React.RefObject<any>;
    removeContactCallback: () => {};
};

const currencyFormatter = new Intl.NumberFormat();

export const DashboardDentalTechRow = (props: TDashboardDentalTechRowProps) => {
    const { userInfo, removeContactCallback, regionList, industryRoles } = props;
    const { navigateRouteClickHandler } = useRouteNavigation();
    const { getImagePath } = useFileManagement();
    const [_logoImg, setLogo] = useState<string | undefined>(undefined);
    const { removeFromMyContactList } = useUserService();
    const goToTechClickHandler = (e: MouseEvent) => {
        navigateRouteClickHandler("dentalTechInfo", e, userInfo.id.toString(), true);
    };

    useEffect(() => {
        if (userInfo?.images?.logo) {
            setLogo(getImagePath(userInfo.images.logo, "fixed", userInfo.guid));
            getCacheKillQueryString();
        } else {
            setLogo(ASSETS_ROUTES.IMGS.techAvatarUrl);
        }
    }, []);
    const deleteContactClickHandler = async (e: MouseEvent, contactUserId: number) => {
        e.preventDefault();
        e.stopPropagation();
        await removeFromMyContactList(contactUserId);
        removeContactCallback();
    };

    return (
        <div className="list-group-item list-group-item-action border border-light py-4">
            <div className="row text-center gap-3">
                <div className="col-md-3 ps-4" style={{ minHeight: "7rem" }} onClick={goToTechClickHandler} role="button">
                    <LogoTech
                        editable={false}
                        userInfo={userInfo}
                        workingState={"done"}
                        isSomePartBeingEdited={false}
                        setInappropriateContent={() => {}}
                        setUnsupportedImageType={() => {}}
                        extraClass="border-light list-logo"
                    />
                </div>
                <div className="col-md-5 text-center text-md-start" onClick={goToTechClickHandler} role="button">
                    <p className="btn-link">
                        <strong>{userInfo.name}</strong>
                    </p>
                    {userInfo.other.roles.length > 0 && (
                        <p className="mb-0">
                            {" "}
                            <small>
                                {userInfo.other.roles
                                    .map((_role) => {
                                        const position = industryRoles.find((role) => role.id === _role.id);
                                        return position ? position.name : null;
                                    })
                                    .join(", ")}
                            </small>
                        </p>
                    )}
                    <p className="mb-0">
                        <small>{userInfo.other.yearsOfExperience} Years Exp.</small>
                    </p>
                    {userInfo.other.desiredRegion !== undefined && (
                        <p className="mb-0">
                            <small>
                                Region of Interest:{" "}
                                {regionList
                                    .filter((region) => userInfo?.other?.desiredRegion?.includes(region.guid))
                                    .map((region) => region.name)
                                    .join(", ")}
                            </small>
                        </p>
                    )}
                    {!!userInfo.other?.desiredSalaryPerYear && (
                        <p className="mb-0">
                            <small>
                                Desired Pay: $
                                {currencyFormatter.format(Number(userInfo.other.desiredSalaryPerYear.amount ?? userInfo.other.desiredSalaryPerYear))}{" "}
                                {userInfo.other.desiredSalaryPerYear.frequency ?? "yearly"}
                            </small>
                        </p>
                    )}

                    {/* <div className="mt-3">
                        <a
                            className="btn-link text-decoration-none"
                            href={getRoute("dentalTechInfo", userInfo.id)}
                            >
                            </a>
                            </div> */}
                </div>
                <div className="col-md-3">
                    <MessageBtn userInfo={userInfo} className="btn-secondary mb-2 w-75 pe-4 " />
                    <button
                        className="btn btn-primary mb-2 w-75 pe-4"
                        onClick={(e) => navigateRouteClickHandler("dentalTechInfo", e, userInfo.id.toString(), true)}
                    >
                        <i className="fa-regular fa-user me-2"></i> View
                    </button>
                    <button onClick={(e) => deleteContactClickHandler(e, userInfo.id)} className="btn btn-outline-danger w-75 ">
                        <i className="fa-regular fa-trash-can me-2"></i>
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
};

//
type TTechListProps = {
    refFilter?: React.RefObject<any>;
    refTechList?: React.RefObject<HTMLDivElement>;
    industryRoles: TGenericIdName[];
};
export const TechList = ({ refFilter, industryRoles, refTechList }: TTechListProps) => {
    const { getTechsFromMyContactList, state, regions } = useTechnicians();
    const [technicians, setTechnicians] = useState<TApiTechnician[]>([]);
    const nameFilter = useRef<string[]>();
    const regionFilters = useRef<string[]>();
    const industryRolesFilters = useRef<number[]>();
    const [totalCount, setTotalCount] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const { setActionsHandler } = useNotificationContext();

    const refreshList = async (offset: number) => {
        const techs = await getTechsFromMyContactList(
            {
                filterRegions: regionFilters.current,
                filterPositions: industryRolesFilters.current,
                filterName: nameFilter.current,
            },
            offset,
        );
        setTechnicians(techs.users);
        setTotalCount(techs.totalCount);
        setPageNumber(offset + 1);
    };

    const resetCallback = () => {
        regionFilters.current = undefined;
        industryRolesFilters.current = undefined;
        nameFilter.current = undefined;
        refreshList(0);
    };

    const filterCallback = async (filterSettings: TTechnicianFilterSettings) => {
        const { regions, industryRoles, names } = filterSettings;

        regionFilters.current = regions ? regions.map((o) => o.data.id) : [];
        industryRolesFilters.current = industryRoles ? industryRoles.map((o) => Number(o.data.id)) : [];
        nameFilter.current = names ? names.map((o) => o.data.name) : [];

        await refreshList(0);
    };

    useEffect(() => {
        setActionsHandler("refreshContactList", () => refreshList(0));
    }, []);

    return (
        <>
            <div className="container-fluid rounded mb-3 pt-3 pb-2" ref={refFilter}>
                <DentalTechSearchFilterPanel filterCallback={filterCallback} resetCallback={resetCallback} searchJustFromContactList={true} />
            </div>
            <h4 className="p-3" ref={refTechList}>
                Job Candidates
            </h4>
            <div className="list-group">
                {state === "working" && (
                    <div className="text-center">
                        <Spinner spinnerColor="text-primary" />
                    </div>
                )}
                {state === "idle" && technicians.length === 0 && <p className="p-3">You don't have any contacts yet.</p>}
                {state === "idle" && technicians.length > 0 && (
                    <>
                        {technicians.map((techInfo) => (
                            <DashboardDentalTechRow
                                key={techInfo.id}
                                userInfo={techInfo}
                                removeContactCallback={() => refreshList(0)}
                                regionList={regions}
                                industryRoles={industryRoles}
                            />
                        ))}
                        <div className="mt-4">
                            {
                                <Pagination
                                    currentPage={pageNumber}
                                    totalCount={totalCount}
                                    itemsPerPage={TECH_SEARCH_PAGINATION_SIZE}
                                    refObject={refTechList}
                                    onPageChange={(offset) => {
                                        refreshList(offset);
                                    }}
                                />
                            }
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export const PendingTechContacts = (props: TDashboardDentalTechRowProps) => {
    const { userInfo, industryRoles, regionList } = props;
    const { navigateRouteClickHandler } = useRouteNavigation();
    // const { removeFromMyContactList } = useUserService();
    const goToTechClickHandler = (e: MouseEvent) => {
        navigateRouteClickHandler("dentalTechInfo", e, userInfo.id.toString(), true);
    };
    /* const deleteContactClickHandler = async (e: MouseEvent, contactUserId: string) => {
         e.preventDefault();
         e.stopPropagation();
 
         await removeFromMyContactList(contactUserId);
         removeContactCallback();
     }*/

    return (
        <div className="list-group-item list-group-item-action border border-light py-4">
            <div className="row text-center gap-3" onClick={goToTechClickHandler} role="button">
                <div className="col-md-3">
                    <LogoTech
                        editable={false}
                        userInfo={userInfo}
                        workingState={"done"}
                        isSomePartBeingEdited={false}
                        setInappropriateContent={() => {}}
                        setUnsupportedImageType={() => {}}
                        extraClass="list-logo"
                    />
                </div>
                <div className="col-md-7 text-center text-md-start">
                    <p className={"btn-link" + (userInfo.incognito ? " lab-can-not-see" : "")}>
                        <strong>{userInfo.name}</strong>
                    </p>
                    <p className="mb-0">
                        <small>{userInfo.other.yearsOfExperience} Years Exp.</small>
                    </p>
                    {userInfo.other.roles && (
                        <small className="">
                            {userInfo.other.roles
                                .map((_role) => {
                                    const position = industryRoles.find((role) => role.id === _role.id);
                                    return position ? position.name : null;
                                })
                                .join(", ")}
                        </small>
                    )}
                    {userInfo.other.desiredRegion !== undefined && (
                        <p className="mb-0">
                            <small>
                                Region of Interest:{" "}
                                {regionList
                                    .filter((region) => userInfo?.other?.desiredRegion?.includes(region.guid))
                                    .map((region) => region.name)
                                    .join(", ")}
                            </small>
                        </p>
                    )}
                    {!!userInfo.other?.desiredSalaryPerYear && (
                        <p className="mb-0">
                            <small>
                                Desired Pay: $
                                {currencyFormatter.format(Number(userInfo.other.desiredSalaryPerYear.amount ?? userInfo.other.desiredSalaryPerYear))}{" "}
                                {userInfo.other.desiredSalaryPerYear.frequency ?? "yearly"}
                            </small>
                        </p>
                    )}
                </div>
                <div className="d-none d-md-flex col-md-1 align-items-center text-end">
                    <i className="fa-solid fa-chevron-right btn-link text-decoration-none"></i>
                </div>
            </div>
        </div>
    );
};
export const UnreadInvitationsList = ({ refPendingList }: { refPendingList?: React.RefObject<any> }) => {
    const { getUnreadInvitations, state, regions, industryRoles } = useTechnicians();
    const [unreadInvitations, setUnreadInvitations] = useState<TApiTechnician[]>([]);
    const nameFilter = useRef<string[]>();
    const regionFilters = useRef<string[]>();
    const industryRolesFilters = useRef<number[]>();
    const [totalCount, setTotalCount] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);

    const refreshList = async (offset: number) => {
        const techs = await getUnreadInvitations(
            {
                filterRegions: regionFilters.current,
                filterPositions: industryRolesFilters.current,
                filterName: nameFilter.current,
            },
            offset,
        );
        setUnreadInvitations(techs.users);
        setTotalCount(techs.totalCount);
        setPageNumber(offset + 1);
    };

    useEffect(() => {
        refreshList(0);
    }, []);
    return (
        <div className="list-group">
            {state === "working" && (
                <div className="text-center">
                    <Spinner spinnerColor="text-primary" />
                </div>
            )}
            {state === "idle" && unreadInvitations.length > 0 && (
                <>
                    {unreadInvitations.map((invitationInfo) => (
                        <PendingTechContacts
                            key={invitationInfo.id}
                            userInfo={invitationInfo}
                            removeContactCallback={() => refreshList(0)}
                            regionList={regions}
                            industryRoles={industryRoles}
                        />
                    ))}
                    <div className="mt-4">
                        {
                            <Pagination
                                currentPage={pageNumber}
                                totalCount={totalCount}
                                itemsPerPage={TECH_SEARCH_PAGINATION_SIZE}
                                refObject={refPendingList}
                                onPageChange={(offset) => {
                                    refreshList(offset);
                                }}
                            />
                        }
                    </div>
                </>
            )}
        </div>
    );
};
